/**
 * UTM + Facebook Click ID Tracking System
 *
 * This utility captures and persists tracking parameters across a multi-page funnel,
 * even when the final form is embedded in an iframe (like GoHighLevel forms).
 *
 * Tracked Parameters:
 * - fbclid (Facebook Click ID)
 * - utm_source, utm_medium, utm_campaign, utm_term, utm_content
 */

// Storage key for localStorage
const STORAGE_KEY = 'funnel_tracking_params'

// Parameters we want to track
const TRACKED_PARAMS = [
  'fbclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
]

/**
 * Interface for tracking parameters
 */
export interface TrackingParams {
  fbclid?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  captured_at?: string // Timestamp when params were first captured
}

/**
 * STEP 1: Capture tracking parameters from URL
 *
 * This should be called on every page load to capture parameters
 * the first time they appear. Subsequent visits won't overwrite
 * existing stored values.
 */
export function captureTrackingParams(): TrackingParams {
  // Get current URL parameters
  const urlParams = new URLSearchParams(window.location.search)

  // Check if we already have stored params
  const existingParams = getStoredTrackingParams()

  // If we already have params and they're recent (within 30 days), don't overwrite
  if (existingParams && existingParams.captured_at) {
    const capturedDate = new Date(existingParams.captured_at)
    const daysSinceCaptured =
      (Date.now() - capturedDate.getTime()) / (1000 * 60 * 60 * 24)

    // If params are less than 30 days old, keep them (don't overwrite)
    if (daysSinceCaptured < 30) {
      return existingParams
    }
  }

  // Capture new params from URL
  const newParams: TrackingParams = {}
  let hasNewParams = false

  TRACKED_PARAMS.forEach((param) => {
    const value = urlParams.get(param)
    if (value) {
      newParams[param as keyof TrackingParams] = value
      hasNewParams = true
    }
  })

  // If we found new params, store them with timestamp
  if (hasNewParams) {
    newParams.captured_at = new Date().toISOString()
    storeTrackingParams(newParams)
    return newParams
  }

  // Return existing params if no new ones found
  return existingParams || {}
}

/**
 * STEP 2: Store tracking parameters in localStorage
 */
function storeTrackingParams(params: TrackingParams): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(params))
  } catch (error) {
    console.error('Failed to store tracking params:', error)
  }
}

/**
 * Get stored tracking parameters from localStorage
 */
export function getStoredTrackingParams(): TrackingParams | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch (error) {
    console.error('Failed to retrieve tracking params:', error)
    return null
  }
}

/**
 * NEW: Get tracking params as URLSearchParams object
 * Used to combine with quiz data for GHL form
 */
export function getTrackingParamsAsURLParams(): URLSearchParams {
  const params = getStoredTrackingParams()
  const urlParams = new URLSearchParams()

  if (!params) return urlParams

  TRACKED_PARAMS.forEach((paramName) => {
    const value = params[paramName as keyof TrackingParams]
    if (value && paramName !== 'captured_at') {
      urlParams.set(paramName, value)
    }
  })

  return urlParams
}

/**
 * NEW: Combine tracking params with quiz data params
 * Returns a single URLSearchParams object with all parameters
 *
 * @param quizParams - URLSearchParams from quiz data
 * @returns Combined URLSearchParams with both tracking and quiz data
 */
export function combineAllParams(quizParams: URLSearchParams): URLSearchParams {
  const trackingParams = getTrackingParamsAsURLParams()
  const combined = new URLSearchParams()

  // Add tracking params first
  trackingParams.forEach((value, key) => {
    combined.set(key, value)
  })

  // Add quiz params (will override tracking params if same key exists)
  quizParams.forEach((value, key) => {
    combined.set(key, value)
  })

  return combined
}

/**
 * STEP 3: Inject tracking parameters into iframe form
 *
 * This attempts two methods:
 * 1. Direct DOM access to fill hidden inputs (if CORS allows)
 * 2. Fallback: append params to iframe src URL
 *
 * @param iframeId - The ID of the iframe element
 * @param additionalParams - Optional additional params to include (e.g., quiz data)
 */
export function injectParamsIntoIframe(
  iframeId: string,
  additionalParams?: URLSearchParams,
): void {
  const trackingParams = getStoredTrackingParams()

  if (!trackingParams || Object.keys(trackingParams).length === 0) {
    console.log('No tracking params to inject')

    // Still try to inject additional params if provided
    if (additionalParams && additionalParams.toString()) {
      const iframe = document.getElementById(iframeId) as HTMLIFrameElement
      if (iframe) {
        appendParamsToIframeSrc(iframe, additionalParams)
      }
    }
    return
  }

  const iframe = document.getElementById(iframeId) as HTMLIFrameElement

  if (!iframe) {
    console.error(`Iframe with id "${iframeId}" not found`)
    return
  }

  // Combine tracking params with additional params
  const allParams = additionalParams
    ? combineAllParams(additionalParams)
    : getTrackingParamsAsURLParams()

  // METHOD 1: Try direct DOM access (works if same-origin or CORS allows)
  tryDirectIframeAccess(iframe, allParams)

  // METHOD 2: Fallback - append to iframe src (always works)
  appendParamsToIframeSrc(iframe, allParams)
}

/**
 * METHOD 1: Direct iframe DOM access
 *
 * Attempts to access the iframe's document and fill hidden inputs.
 * This only works if CORS policy allows it (same-origin or proper headers).
 */
function tryDirectIframeAccess(
  iframe: HTMLIFrameElement,
  params: URLSearchParams,
): void {
  // Wait for iframe to load, then try to access its document
  const attemptInterval = setInterval(() => {
    try {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document

      if (!iframeDoc) {
        return // Not ready yet, keep trying
      }

      // Check if form exists in iframe
      const form = iframeDoc.querySelector('form')

      if (!form) {
        return // Form not loaded yet, keep trying
      }

      // Success! We can access the iframe content
      clearInterval(attemptInterval)

      // Fill hidden inputs for each parameter
      params.forEach((value, paramName) => {
        // Look for existing hidden input
        let input = iframeDoc.querySelector(
          `input[name="${paramName}"]`,
        ) as HTMLInputElement

        // If it doesn't exist, create it
        if (!input) {
          input = iframeDoc.createElement('input')
          input.type = 'hidden'
          input.name = paramName
          form.appendChild(input)
        }

        // Set the value
        input.value = value
        console.log(`Injected ${paramName}=${value} into iframe form`)
      })
    } catch (error) {
      // CORS error - can't access iframe content
      // This is expected for cross-origin iframes
      clearInterval(attemptInterval)
      console.log(
        'Direct iframe access blocked by CORS (expected for GHL forms)',
      )
    }
  }, 100) // Check every 100ms

  // Stop trying after 5 seconds
  setTimeout(() => clearInterval(attemptInterval), 5000)
}

/**
 * METHOD 2: Append parameters to iframe src URL
 *
 * This is the fallback method that always works.
 * GHL forms can read these parameters natively from the URL.
 */
function appendParamsToIframeSrc(
  iframe: HTMLIFrameElement,
  params: URLSearchParams,
): void {
  const currentSrc = iframe.src

  if (!currentSrc) return

  // Parse current URL
  const url = new URL(currentSrc)

  // Add each parameter to the URL
  params.forEach((value, paramName) => {
    // Only add if not already present
    if (!url.searchParams.has(paramName)) {
      url.searchParams.set(paramName, value)
    }
  })

  // Update iframe src with new URL
  iframe.src = url.toString()
  console.log('Appended all params to iframe src (fallback method)')
}

/**
 * STEP 4: Clear tracking parameters
 *
 * Call this on the thank-you/confirmation page to clear stored params
 * so they don't persist for future unrelated conversions.
 */
export function clearTrackingParams(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
    console.log('Tracking params cleared')
  } catch (error) {
    console.error('Failed to clear tracking params:', error)
  }
}

/**
 * Get tracking params as URL query string
 * Useful for appending to links or form actions
 */
export function getTrackingParamsAsQueryString(): string {
  const params = getStoredTrackingParams()

  if (!params) return ''

  const queryParams = new URLSearchParams()

  TRACKED_PARAMS.forEach((paramName) => {
    const value = params[paramName as keyof TrackingParams]
    if (value && paramName !== 'captured_at') {
      queryParams.set(paramName, value)
    }
  })

  const queryString = queryParams.toString()
  return queryString ? `?${queryString}` : ''
}
